import React, { useState, useRef, useEffect } from 'react';
import axios from 'axios';
import { MessageCircle, X, Send, ArrowRight, Loader } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const generateSessionId = () => `chat_${Date.now()}_${Math.random().toString(36).slice(2)}`;

const WELCOME_EN = `Hi! I'm **DISCO**, the TOTAL COMPOSITES little helper!\n\nI can help you with products, pricing, ordering, expeditions, and anything else about TOTAL COMPOSITES.\n\nBefore we start, could you share your name and email?`;
const WELCOME_FR = `Bonjour ! Je suis **DISCO**, le petit assistant de TOTAL COMPOSITES !\n\nJe peux vous aider avec les produits, les prix, les commandes, les expéditions et tout ce qui concerne TOTAL COMPOSITES.\n\nAvant de commencer, pourriez-vous partager votre nom et votre courriel ?`;

const formatMessage = (text) => {
  // Convert **bold**, links, and line breaks
  return text
    .split('\n')
    .map((line, i) => {
      // Convert markdown links [text](url)
      const parts = line.split(/(\[.*?\]\(.*?\))/g);
      return (
        <span key={i}>
          {parts.map((part, j) => {
            const linkMatch = part.match(/\[(.+?)\]\((.+?)\)/);
            if (linkMatch) {
              return <a key={j} href={linkMatch[2]} style={{ color: '#E8520A', textDecoration: 'underline' }} onClick={e => { if (linkMatch[2].startsWith('/')) { e.preventDefault(); window.location.href = linkMatch[2]; } }}>{linkMatch[1]}</a>;
            }
            // Bold
            const boldParts = part.split(/(\*\*.+?\*\*)/g);
            return boldParts.map((bp, k) => bp.startsWith('**') ? <strong key={k}>{bp.slice(2, -2)}</strong> : bp);
          })}
          {i < text.split('\n').length - 1 && <br />}
        </span>
      );
    });
};

const ChatWidget = () => {
  const [open, setOpen] = useState(false);
  const [phase, setPhase] = useState('intro'); // 'intro' | 'chat'
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [introError, setIntroError] = useState('');
  const { language, t } = useLanguage();
  const [messages, setMessages] = useState([{ role: 'assistant', text: language === 'fr' ? WELCOME_FR : WELCOME_EN }]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [sessionId] = useState(generateSessionId);
  const [unread, setUnread] = useState(0);
  const bottomRef = useRef(null);
  const inputRef = useRef(null);
  const [heroVisible, setHeroVisible] = useState(true);

  useEffect(() => {
    const onScroll = () => setHeroVisible(window.scrollY < window.innerHeight * 0.6);
    window.addEventListener('scroll', onScroll, { passive: true });
    onScroll();
    return () => window.removeEventListener('scroll', onScroll);
  }, []);



  useEffect(() => {
    if (open) { setUnread(0); setTimeout(() => inputRef.current?.focus(), 100); }
  }, [open]);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
    if (!open && messages.length > 1 && messages[messages.length - 1].role === 'assistant') {
      setUnread(u => u + 1);
    }
  }, [messages, open]);

  const handleIntroSubmit = (e) => {
    e.preventDefault();
    if (!name.trim()) { setIntroError(language === 'fr' ? 'Veuillez entrer votre nom.' : 'Please enter your name.'); return; }
    if (!email.trim() || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) { setIntroError(language === 'fr' ? 'Veuillez entrer un courriel valide.' : 'Please enter a valid email.'); return; }
    setIntroError('');
    setPhase('chat');
    setMessages(prev => [
      ...prev,
      { role: 'assistant', text: language === 'fr' 
        ? `Enchanté, **${name.trim()}** ! Comment puis-je vous aider aujourd'hui ?\n\nVous pouvez me poser des questions sur nos produits, les prix, comment commander, nos expéditions, les ressources constructeurs, ou tout ce qui concerne TOTAL COMPOSITES.`
        : `Great to meet you, **${name.trim()}**! How can I help you today?\n\nYou can ask me about our products, pricing, how to order, our expeditions, builder resources, or anything else about TOTAL COMPOSITES.` }
    ]);
  };

  const sendMessage = async (text) => {
    if (!text.trim() || loading) return;
    const userMsg = text.trim();
    setInput('');
    setMessages(prev => [...prev, { role: 'user', text: userMsg }]);
    setLoading(true);
    try {
      const { data } = await axios.post(`${BACKEND_URL}/api/chat`, {
        session_id: sessionId,
        name: name.trim(),
        email: email.trim(),
        message: userMsg,
        language: language,
      });
      setMessages(prev => [...prev, { role: 'assistant', text: data.response }]);
    } catch (err) {
      console.error('DISCO chat error:', err?.response?.data || err?.message || err);
      const errMsg = err?.response?.status === 500
        ? (language === 'fr' ? 'J\'ai un petit problème — veuillez réessayer dans un moment.' : 'I\'m having a small hiccup — please try again in a moment.')
        : (language === 'fr' ? 'Problème de connexion. Veuillez vérifier votre connexion internet et réessayer, ou [contactez-nous](/contact) directement.' : 'Connection issue. Please check your internet and try again, or [contact us](/contact) directly.');
      setMessages(prev => [...prev, { role: 'assistant', text: errMsg }]);
    } finally {
      setLoading(false);
      setTimeout(() => inputRef.current?.focus(), 50);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); sendMessage(input); }
  };

  const QUICK = language === 'fr'
    ? ['Parlez-moi des campeurs portés 2026', 'Quels sont les prix ?', 'Comment commander ?', 'Info partenariat constructeur']
    : ['Tell me about 2026 Truck Campers', 'What are the prices?', 'How do I order?', 'Builder partnership info'];

  return (
    <>
      {/* Toggle button — hidden while hero in view */}
      <button
        onClick={() => setOpen(o => !o)}
        className="fixed z-50 flex items-center justify-center shadow-2xl transition-all duration-500 hover:scale-110"
        style={{
          bottom: 24, right: 24,
          width: 56, height: 56,
          borderRadius: '50%',
          backgroundColor: '#E8520A',
          boxShadow: '0 4px 24px rgba(232,82,10,0.45)',
          opacity: heroVisible && !open ? 0 : 1,
          pointerEvents: heroVisible && !open ? 'none' : 'all',
          transform: heroVisible && !open ? 'scale(0.7) translateY(10px)' : 'scale(1) translateY(0)',
        }}
      >
        {open ? <X size={22} className="text-white" /> : <img src="/disco_avatar.png" alt="DISCO" className="w-10 h-10 rounded-full object-cover" />}
        {!open && unread > 0 && (
          <span className="absolute -top-1 -right-1 w-5 h-5 rounded-full bg-red-500 text-white text-xs flex items-center justify-center font-bold">{unread}</span>
        )}
      </button>

      {/* Chat window */}
      {open && (
        <div
          className="fixed z-50 flex flex-col overflow-hidden shadow-2xl"
          style={{
            bottom: 92, right: 24,
            width: 360, height: 540,
            backgroundColor: '#0a0a0a',
            border: '1px solid rgba(255,255,255,0.08)',
            borderRadius: 12,
            boxShadow: '0 8px 60px rgba(0,0,0,0.7), 0 0 0 1px rgba(255,255,255,0.06)',
          }}
        >
          {/* Header */}
          <div className="flex items-center gap-3 px-4 py-3.5 flex-shrink-0" style={{ borderBottom: '1px solid rgba(255,255,255,0.07)', backgroundColor: '#111' }}>
          <img src="/disco_avatar.png" alt="DISCO" className="w-8 h-8 rounded-full object-cover flex-shrink-0" />
            <div className="flex-1 min-w-0">
              <p className="text-white text-xs font-bold uppercase tracking-widest">DISCO</p>
              <p className="text-xs" style={{ color: 'rgba(255,255,255,0.35)' }}>TOTAL COMPOSITES · {language === 'fr' ? 'Petit assistant' : 'Little Helper'} 🐾</p>
            </div>
            <button onClick={() => setOpen(false)} className="text-gray-600 hover:text-white transition-colors flex-shrink-0">
              <X size={16} />
            </button>
          </div>

          {/* Phase: intro form */}
          {phase === 'intro' && (
            <div className="flex-1 overflow-y-auto p-4 flex flex-col gap-4">
              {/* Welcome message */}
              <div className="flex gap-2.5">
                <img src="/disco_avatar.png" alt="DISCO" className="w-6 h-6 rounded-full object-cover flex-shrink-0 mt-0.5" />
                <div className="rounded-2xl rounded-tl-sm px-3.5 py-2.5 text-xs leading-relaxed max-w-xs" style={{ backgroundColor: '#1a1a1a', color: 'rgba(255,255,255,0.8)' }}>
                  {formatMessage(language === 'fr' ? WELCOME_FR : WELCOME_EN)}
                </div>
              </div>

              {/* Name + Email form */}
              <form onSubmit={handleIntroSubmit} className="mt-auto space-y-2.5">
                {introError && <p className="text-xs text-red-400">{introError}</p>}
                <input
                  type="text"
                  placeholder={language === 'fr' ? 'Votre nom *' : 'Your name *'}
                  value={name}
                  onChange={e => setName(e.target.value)}
                  className="w-full px-3.5 py-2.5 text-xs text-white rounded-lg focus:outline-none"
                  style={{ backgroundColor: '#1a1a1a', border: '1px solid rgba(255,255,255,0.1)' }}
                  onFocus={e => (e.target.style.borderColor = '#E8520A')}
                  onBlur={e => (e.target.style.borderColor = 'rgba(255,255,255,0.1)')}
                />
                <input
                  type="email"
                  placeholder={language === 'fr' ? 'Votre courriel *' : 'Your email *'}
                  value={email}
                  onChange={e => setEmail(e.target.value)}
                  className="w-full px-3.5 py-2.5 text-xs text-white rounded-lg focus:outline-none"
                  style={{ backgroundColor: '#1a1a1a', border: '1px solid rgba(255,255,255,0.1)' }}
                  onFocus={e => (e.target.style.borderColor = '#E8520A')}
                  onBlur={e => (e.target.style.borderColor = 'rgba(255,255,255,0.1)')}
                />
                <button
                  type="submit"
                  className="w-full py-2.5 text-xs font-bold uppercase tracking-widest text-white rounded-lg flex items-center justify-center gap-2 transition-opacity hover:opacity-85"
                  style={{ backgroundColor: '#E8520A' }}
                >
                  {language === 'fr' ? 'Démarrer la discussion' : 'Start Chat'} <ArrowRight size={13} />
                </button>
              </form>
            </div>
          )}

          {/* Phase: chat */}
          {phase === 'chat' && (
            <>
              {/* Messages */}
              <div className="flex-1 overflow-y-auto p-4 space-y-3" style={{ scrollbarWidth: 'none' }}>
                {messages.map((msg, i) => (
                  <div key={msg.id || `msg-${i}`} className={`flex gap-2.5 ${msg.role === 'user' ? 'flex-row-reverse' : ''}`}>
                    {msg.role === 'assistant' && (
                      <div className="w-6 h-6 rounded-full flex-shrink-0 flex items-center justify-center mt-0.5" style={{ backgroundColor: '#E8520A' }}>
                        <MessageCircle size={11} className="text-white" />
                      </div>
                    )}
                    <div
                      className="rounded-2xl px-3.5 py-2.5 text-xs leading-relaxed max-w-xs"
                      style={{
                        backgroundColor: msg.role === 'user' ? '#E8520A' : '#1a1a1a',
                        color: msg.role === 'user' ? '#fff' : 'rgba(255,255,255,0.8)',
                        borderRadius: msg.role === 'user' ? '16px 16px 4px 16px' : '16px 16px 16px 4px',
                      }}
                    >
                      {formatMessage(msg.text)}
                    </div>
                  </div>
                ))}

                {loading && (
                  <div className="flex gap-2.5">
                    <img src="/disco_avatar.png" alt="DISCO" className="w-6 h-6 rounded-full object-cover flex-shrink-0 mt-0.5" />
                    <div className="rounded-2xl px-3.5 py-2.5 flex items-center gap-1.5" style={{ backgroundColor: '#1a1a1a' }}>
                      {[0, 1, 2].map(d => (
                        <div key={d} className="w-1.5 h-1.5 rounded-full animate-bounce" style={{ backgroundColor: 'rgba(255,255,255,0.4)', animationDelay: `${d * 150}ms` }} />
                      ))}
                    </div>
                  </div>
                )}

                {/* Quick replies — only after first assistant msg in chat */}
                {!loading && messages.length === 2 && (
                  <div className="flex flex-wrap gap-1.5 mt-2">
                    {QUICK.map((q, i) => (
                      <button key={q} onClick={() => sendMessage(q)}
                        className="text-xs px-2.5 py-1.5 rounded-full border transition-all hover:border-orange-500 hover:text-orange-400"
                        style={{ borderColor: 'rgba(255,255,255,0.15)', color: 'rgba(255,255,255,0.6)', backgroundColor: 'transparent' }}>
                        {q}
                      </button>
                    ))}
                  </div>
                )}

                <div ref={bottomRef} />
              </div>

              {/* Input */}
              <div className="flex-shrink-0 p-3" style={{ borderTop: '1px solid rgba(255,255,255,0.07)' }}>
                <div className="flex items-end gap-2 rounded-xl px-3 py-2" style={{ backgroundColor: '#1a1a1a', border: '1px solid rgba(255,255,255,0.1)' }}>
                  <textarea
                    ref={inputRef}
                    rows={1}
                    value={input}
                    onChange={e => setInput(e.target.value)}
                    onKeyDown={handleKeyDown}
                    placeholder={language === 'fr' ? 'Posez une question sur TOTAL COMPOSITES…' : 'Ask anything about TOTAL COMPOSITES…'}
                    className="flex-1 bg-transparent text-xs text-white placeholder-gray-600 focus:outline-none resize-none"
                    style={{ maxHeight: 80, lineHeight: 1.5 }}
                  />
                  <button
                    onClick={() => sendMessage(input)}
                    disabled={!input.trim() || loading}
                    className="flex-shrink-0 w-7 h-7 rounded-lg flex items-center justify-center transition-all disabled:opacity-30"
                    style={{ backgroundColor: '#E8520A' }}
                  >
                    {loading ? <Loader size={13} className="text-white animate-spin" /> : <Send size={13} className="text-white" />}
                  </button>
                </div>
                <p className="text-center text-xs mt-2" style={{ color: 'rgba(255,255,255,0.18)' }}>
                  Powered by DISCO · TOTAL COMPOSITES {language === 'fr' ? 'IA' : 'AI'}
                </p>
              </div>
            </>
          )}
        </div>
      )}
    </>
  );
};

export default ChatWidget;
